/**
 * Readers for the query string of a notification link.
 *
 * A notification link carries the submitter's token and, when a strict-order form
 * produced several submissions, the submission the submitter is being asked for.
 *
 * Links sent before the separator was fixed joined the token onto the query with a
 * second '?' ("?submission=12?token=abc"), which makes URLSearchParams read the
 * token as part of the submission id. Those links are still in people's inboxes,
 * so both shapes have to keep parsing.
 */

const LEGACY_TOKEN_SEPARATOR = '?token=';

/**
 * Reads the submitter token out of a location search string.
 *
 * @param {string} [search] - The location search string, e.g. '?submission=12&token=abc'.
 * @returns {string|null} - The token, or `null` when the link carries none.
 */
export const getTokenFromSearch = (search = '') => {
  const token = new URLSearchParams(search).get('token');

  if (token) {
    return token;
  }

  if (search.includes(LEGACY_TOKEN_SEPARATOR)) {
    return search.split(LEGACY_TOKEN_SEPARATOR)[1] || null;
  }

  return null;
};

/**
 * Reads the submission id out of a location search string.
 *
 * @param {string} [search] - The location search string, e.g. '?submission=12&token=abc'.
 * @returns {string|null} - The submission id, or `null` when the link names none.
 */
export const getSubmissionIdFromSearch = (search = '') => {
  const submission = new URLSearchParams(search).get('submission');

  if (!submission) {
    return null;
  }

  // A legacy link leaves the token glued to the id: "12?token=abc".
  return submission.split('?')[0] || null;
};
